import { CommonModule, formatDate } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCard, MatCardModule } from '@angular/material/card';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DataTableFilter } from '../../../../../shared/components/data-table/filters/components/DataTableFilter';
import { Policy } from '../../../../policies/models/Policy';
import { ReservationFilterFromDateComponent } from '../../../../reservations/components/filters/reservation-filter-from-date/reservation-filter-from-date.component';
import { ReservationFilterToDateComponent } from '../../../../reservations/components/filters/reservation-filter-to-date/reservation-filter-to-date.component';
import { ReservationStatusFilterComponent } from '../../../../reservations/components/filters/reservation-status-filter/reservation-status-filter.component';
import { ReservationTableRow } from '../../../../reservations/models/ReservationTableRow';
import { UserContextProvider } from '../../../../../infrastructure/context/user-context-provider';
import { SearchService } from '../../../../../shared/search/search.service';
import { NotificationService } from '../../../../../utilities/PortalNotificationUtility';
import { ClaimsService } from '../../../../claims/services/claims.service';
import { OrganizationService } from '../../../../organizations/services/organizations.service';
import { ReservationFilterStateService } from '../../../../reservations/services/reservation-filter-state.service';
import { ReservationMessageService } from '../../../../reservations/services/reservation-message.service';
import { ReservationService } from '../../../../reservations/services/reservation.service';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { ColorPropType, ColorProp, getColor } from '../../../../../layout/Colors';
import { FilterRangeType, SortParams, FilterParams } from '../../../../../shared/search/SearchParams';
import { dateFormat } from '../../../../../utilities/DateUtility';
import { UpdatePolicyToolDialogComponent } from '../../../../policies/components/update-policy-tool-dialog/update-policy-tool-dialog.component';
import { UpdateReservationStatusDialogComponent } from '../../../../reservations/components/update-reservation-status-dialog/update-reservation-status-dialog.component';
import { PropertyReservationsColumns, Reservation } from '../../../../reservations/models/Reservation';
import { ReservationSearchRecord } from '../../../../reservations/models/ReservationSearchRecord';
import { canFileClaimForReservation, getFormattedReservationStatus, getReservationStatus as getReservationSearchRecordStatus } from '../../../../reservations/reservation-detail/ReservationHelper';
import { DataTableComponent } from '../../../../../shared/components/data-table/data-table.component';

@Component({
  selector: 'app-property-reservations',
  imports: [
    CommonModule,
    MatCardModule,
    DataTableComponent,
    MatButtonModule,
    MatCard, 
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule
  ],
  templateUrl: './property-reservations.component.html',
  styleUrl: './property-reservations.component.scss'
})
export class PropertyReservationsComponent implements AfterViewInit, OnDestroy {

  @Input() propertyId!: number;
  @Input() propertyOrganizationId!: number;
  @ViewChild(DataTableComponent, { static: false }) dataTableComponent: any;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  private filterEventSubscription?: Subscription;

  filterDefinitions: DataTableFilter[] = [
    { id: 'reservation-filter-status', name: 'Reservation Status', shortName: 'Status', component: ReservationStatusFilterComponent },
    { id: 'reservation-filter-arrival-date', name: 'Arrival Date', shortName: 'Arrival Date', component: ReservationFilterFromDateComponent },
    { id: 'reservation-filter-departure-date', name: 'Departure Date', shortName: 'Departure Date', component: ReservationFilterToDateComponent }
  ];

  displayedColumns = PropertyReservationsColumns;
  displayedColumnNames: string[] = [];

  tableToApiColumnMap: { [key: string]: string } = {
    'reservationId': 'reservationId',
    'referenceId': 'referenceId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'propertyName': 'propertyName',
    'dates': 'arrivalDate',
    'bookingDate': 'bookingDate',
    'fullName': 'fullName',
    'pillStatus': 'status',
    'action': ''
  }

  apiToTableColumnMap: { [key: string]: string } = {
    'reservationId': 'reservationId',
    'referenceId': 'referenceId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'propertyName': 'propertyName',
    'arrivalDate': 'dates',
    'bookingDate': 'bookingDate',
    'fullName': 'fullName',
    'status': 'pillStatus',
    'action': ''
  }

  dataSource = new MatTableDataSource<ReservationTableRow>();
  mappedReservations: ReservationTableRow[] = [];
  loading = new BehaviorSubject<boolean>(false);
  tableLength = 0;
  pageSize = 10;
  pageNumber!: number;
  keyword = '';

  reservationStatusUpdate: boolean = false;
  organizationPolicies: Policy[] = [];
  insurancePolicyIdArr: any[] = [];
  filteredPolicyIdArray: any[] = [];
  reservationsToUpdate: any[] = [];
  loadingPolicies: boolean = false;

  getFormattedReservationStatus = getFormattedReservationStatus;

  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);
  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);

  constructor(
    private router: Router,
    private reservationService: ReservationService,
    private reservationMessageService: ReservationMessageService,
    private reservationFilterStateService: ReservationFilterStateService,
    private searchService: SearchService,
    private claimsService: ClaimsService,
    private organizationService: OrganizationService,
    private userContextProvider: UserContextProvider,
    private logService: Logger,
    private notificationService: NotificationService,
    private dialogRef: MatDialog,
    private matSnackbar: MatSnackBar
  ) { }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  ngOnDestroy() {
    this.filterEventSubscription?.unsubscribe();
    this.searchService.removeSearchAndSortQueries();
    this.searchService.resetQuery();
  }

  loadPropertyReservationsTab() {
    this.searchService.removeSearchAndSortQueries();
    this.searchService.resetQuery();
    this.searchService.setPageSize(this.pageSize);

    this.initTableColumns();
    this.loadPage();

    this.setSearchQuery();
    this.setupFilterState();
    this.setupSortState();
    this.setupFilterEventHandler();
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  setSearchQuery() {
    if (this.searchService.currentQuery.sortParams?.sortField === 'reservationId') {
      if (this.searchService.currentQuery.page > 1) {
        this.searchService.setPage(this.searchService.currentQuery.page);
        this.pageNumber = this.searchService.currentQuery.page;
        this.initialPageNumber = new BehaviorSubject<number>(this.searchService.currentQuery.page);
      } else {
        this.searchService.setPage(1);
        this.pageNumber = 1;
      }
    } else {
      this.searchService.resetQuery();
    }
    this.searchService.setSort("reservationId", -1);
  }

  private setupFilterState() {
    if (this.reservationService.lastSearchRequest?.filterParams?.find(f => f.fieldName == 'arrivalDate')) {
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.arrivalDate, 'arrivalDate', FilterRangeType.GREATER_THAN);
    }
    if (this.reservationService.lastSearchRequest?.filterParams?.find(f => f.fieldName == 'departureDate')) {
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.departureDate, 'departureDate', FilterRangeType.LESS_THAN);
    }
    if (this.reservationService.lastSearchRequest?.filterParams?.find(f => f.fieldName == 'status')) {
      this.updateAllStatusFilters();
    }
  }

  private setupSortState() {
    const sort = this.reservationService.lastSearchRequest?.sortParams;
    if (sort) {
      this.searchService.addSort(sort);
      const sortField = this.getSortPropertyNameForTable(sort.sortField);
      const sortDirection = sort.sortOrder == 1 ? 'asc' : 'desc';
      this.initialSort.next({ active: sortField, direction: sortDirection });
    } else {
      this.initialSort.next({ active: 'reservationId', direction: 'desc' });
    }
  }

  private getSortPropertyNameForTable(field: string) {
    return this.apiToTableColumnMap[field];
  }

  private setupFilterEventHandler() {
    this.filterEventSubscription?.unsubscribe();
    this.filterEventSubscription = this.reservationMessageService.applyFilterEvent.subscribe(_ => {
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.arrivalDate, 'arrivalDate', FilterRangeType.GREATER_THAN);
      this.updateDateFilterAfterFilterApplied(this.reservationFilterStateService.departureDate, 'departureDate', FilterRangeType.LESS_THAN);
      this.updateAllStatusFilters();
      this.loadPage();
    });
  }

  private updateAllStatusFilters() {
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.upcoming, 'Upcoming');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.isHome, 'In Home');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.completed, 'Complete');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.canceled, 'Canceled');
    this.updateStatusFilterAfterFilterApplied(this.reservationFilterStateService.claimFiled, 'Claim Filed');
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      this.searchService.setOrganizationId(this.propertyOrganizationId);
      this.searchService.replaceFilter({ fieldName: 'propertyIdText', value: [String(this.propertyId)], range: false });

      const { reservations, totalRecords } = await this.reservationService.searchReservations(this.searchService.currentQuery);
      this.mappedReservations = (await Promise.allSettled(reservations.map((r:any) => this.buildReservationTableRow(r))))
        .filter(r => r.status === 'fulfilled')
        .map(r => r.value);
      this.updateTableDataSource(this.mappedReservations, totalRecords);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Property Reservations', 'Error while retrieving Property Reservations');
    } finally {
      this.loading.next(false);
    }
  }

  async buildReservationTableRow(reservation: ReservationSearchRecord): Promise<ReservationTableRow> {
    const reservationDates = this.buildReservationTableRowDates(reservation);

    let canFileClaim: boolean = false;
    canFileClaim = this.canFileClaim(reservation);

    let latestClaimId;
    if (reservation.claimCount > 0) {
      latestClaimId = await this.getLatestClaimIdForReservation(reservation.reservationId);
    }

    return {
      id: reservation.reservationId,
      referenceId: reservation.referenceId ?? '',
      reservationId: reservation.reservationIdText,
      organizationId: reservation.organizationId,
      organizationName: reservation.organizationName,
      propertyName: reservation.propertyName,
      pillStatus: String(getReservationSearchRecordStatus(reservation)),
      dates: `${reservationDates.arrivalDate} - ${reservationDates.departureDate}`,
      bookingDate: reservationDates.bookingDate,
      fullName: reservation.fullName,
      action: canFileClaim,
      departureDate: reservation.departureDate,
      latestClaimId: latestClaimId,
      arrivalDate: reservation.arrivalDate
    }
  }

  private buildReservationTableRowDates(reservation: ReservationSearchRecord): { arrivalDate: string, departureDate: string, bookingDate: string } {
    let arrivalDate = '';
    let departureDate = '';
    let bookingDate = '';

    try {
      if (reservation.arrivalDate) {
        arrivalDate = dateFormat(reservation.arrivalDate, this.userContextProvider.region);
      }

      if (reservation.departureDate) {
        departureDate = dateFormat(reservation.departureDate, this.userContextProvider.region);
      }

      if (reservation.bookingDate) {
        bookingDate = dateFormat(reservation.bookingDate, this.userContextProvider.region);
      }
    } catch (e) {
      this.logService.handleError(`Unable to convert Booking Date: ${reservation.bookingDate}, Arrival Date: ${reservation.arrivalDate} or Departure Date: ${reservation.departureDate} to a date`, `Unable to convert Booking, Arrival or Departure Date`, `Unable to convert Booking Date: ${reservation.bookingDate}, Arrival Date: ${reservation.arrivalDate} or Departure Date: ${reservation.departureDate} to a date`);
    }

    return {
      arrivalDate,
      departureDate,
      bookingDate
    }
  }

  updateTableDataSource(reservations: ReservationTableRow[], totalRecords: number) {
    this.dataSource = new MatTableDataSource(reservations);
    this.tableLength = totalRecords;
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  onCreateReservationClick() {
    this.router.navigate(['/reservations/form']);
  }

  onPageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadPage();
  }

  onSearchEvent(keyword: string): void {
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
    this.loadPage();
  }

  onSortEvent(sortParams: SortParams) {
    sortParams.sortField = this.tableToApiColumnMap[sortParams.sortField];
    this.searchService.addSort(sortParams);
    this.loadPage();
  }

  onReservationActionClick(reservation: ReservationTableRow) {
    this.handleNewClaimButtonClicked(reservation);
  }

  private handleNewClaimButtonClicked(reservation: ReservationTableRow) {
    if (reservation.latestClaimId) {
      this.router.navigate([`/claims/detail/${reservation.latestClaimId}`]);
    } else {
      const reservationId = reservation.id;
      this.router.navigate(['/claims/form'], { queryParams: { reservationId: reservationId } });
    }
  }

  getReservationStatusColor(status: string): string {
    let reservationStatusColorProp: ColorPropType = ColorProp.ReservationStatusDefault;

    switch (status.toUpperCase()) {
      case 'UPCOMING':
        reservationStatusColorProp = ColorProp.ReservationStatusUpcoming;
        break;
      case 'IN HOME':
        reservationStatusColorProp = ColorProp.ReservationStatusInHome;
        break;
      case 'COMPLETE':
        reservationStatusColorProp = ColorProp.ReservationStatusComplete;
        break;
      case 'CLAIM FILED':
        reservationStatusColorProp = ColorProp.ReservationStatusClaimFiled;
        break;
      case 'CANCELED':
        reservationStatusColorProp = ColorProp.ReservationStatusCanceled;
        break;
    }

    return getColor(reservationStatusColorProp);
  }

  updateStatusFilterAfterFilterApplied(statusEnabled: boolean, statusName: string) {
    if (statusEnabled) {
      this.updateStatusFilter(statusName);
    } else {
      this.removeStatusFilterValue(statusName);
    }
  }

  removeStatusFilterValue(statusValue: string) {
    this.searchService.removeValueFromFilter('status', statusValue);
  }

  updateStatusFilter(statusValue: string) {
    this.searchService.updateFilter({
      fieldName: 'status',
      value: [statusValue],
      range: false
    });
  }

  updateDateFilterAfterFilterApplied(dateValue: string, filterName: string, rangeType: number) {
    if (dateValue) {
      this.addDateFilter(dateValue, filterName, rangeType);
    } else {
      this.removeDateFilter(filterName);
    }
  }

  addDateFilter(dateValue: string, filterName: string, rangeType: number) {
    if (!dateValue || !filterName) {
      return;
    }

    const formattedDate = formatDate(dateValue, 'yyyy-MM-dd', 'en-US', 'UTC');
    const filter: FilterParams = {
      fieldName: filterName,
      value: [formattedDate],
      range: true,
      rangeType: rangeType
    };
    this.searchService.replaceFilter(filter);
  }

  removeDateFilter(filterName: string) {
    this.searchService.removeFilter(filterName);
  }

  private canFileClaim(reservation: ReservationSearchRecord): boolean {
    const status = getReservationSearchRecordStatus(reservation);
    if (!status) {
      this.logService.handleError(`Unable to determine reservation status for reservation ${reservation.reservationId}`, 'Unable to determine reservation status', `Unable to determine reservation status for reservation ${reservation.reservationId}`);
      return false;
    }

    return canFileClaimForReservation(reservation.departureDate, status, this.userContextProvider.isNaUser());
  }

  getNewClaimButtonBackgroundColor(row: ReservationTableRow): string {
    if (!row.departureDate) {
      return '';
    }

    return getColor(ColorProp.NewClaimAfter60Days);
  }

  private async getLatestClaimIdForReservation(reservationId: number): Promise<string> {
    try {
      const response = await this.claimsService.getClaims({
        filter: { draft: true, submitted: true, completed: true },
        page: 1,
        pageSize: 1,
        reservationId: reservationId
      });
      if (response?.totalRecords > 0 && response?.claims?.[0]?.id) {
        return response.claims[0].id;
      } else {
        return '';
      }
    } catch (e) {
      this.logService.handleError(e, `Error while calculating new Claim button text for reservation`, `Error while calculating new Claim button text for reservation: ${reservationId}`);
    }

    return '';
  }

  getActionButtonText(reservation: ReservationTableRow): string {
    return reservation.latestClaimId ? `Claim ${reservation.latestClaimId}` : 'New Claim';
  }

  getActionButtonIcon(reservation: ReservationTableRow): string {
    return reservation.latestClaimId ? 'edit' : 'add';
  }


  // ADMIN
  async getOrganizationPolicies(organizationId: number) {
    this.organizationPolicies = await this.organizationService.getPoliciesByOrganizationId(organizationId);
  }

  async getSelectedReservationDetails(reservations: any[]) {
    this.reservationsToUpdate = [];

    try {
      this.loadingPolicies = true;
      this.organizationPolicies = [];
      this.organizationPolicies = await this.organizationService.getPoliciesByOrganizationId(reservations[0].organizationId);

      this.reservationsToUpdate = await Promise.all(
        reservations.map(reservation => this.reservationService.getReservationById(reservation.id))
      );

      if (this.organizationPolicies && this.reservationStatusUpdate) {
        this.openUpdateReservationStatusDialog();
      } else if (this.organizationPolicies && !this.reservationStatusUpdate) {
        this.openAssignReservationPolicyDialog();
      }
    } catch(e) {
      this.logService.handleError(e, 'No Organization policies found', 'No Organization policies found');
    } finally {
      this.loadingPolicies = false;
    }
  }

  onReservationUpdateStatusEvent(row: any) {
    this.reservationStatusUpdate = true;
    let reservationsSelected = row.selected;
    this.getSelectedReservationDetails(reservationsSelected);
  }

  openUpdateReservationStatusDialog() {
    const statusDialogRef = this.dialogRef.open(UpdateReservationStatusDialogComponent, {
      width: '60%',
      maxHeight: '700px',
      disableClose: true,
      data: this.reservationsToUpdate
    });
    statusDialogRef.afterClosed().subscribe(statusPayload => {
      if (statusPayload.statusId > 0) {
        try {
          this.reservationsToUpdate.forEach(reservation => {
            const updatePayload = this.buildUpdatePayload(statusPayload, reservation);
            const infoPayload = this.buildInfoPayload(reservation);

            this.reservationService.updateReservationStatus(reservation, updatePayload).then(_ => {
              this.reservationService.updateReservationInfo(reservation, infoPayload).then(async _ => {
                await this.loadPage();
              });
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Reservation status', 'Error while updating Reservation status');
        }
      }
    });
  }

  buildUpdatePayload(statusPayload: any, reservation: any) {
    const updatePayload = {
      id: reservation.reservation_id.toString(),
      status_date: statusPayload.statusDate,
      status_id: statusPayload.statusId
    }
    return updatePayload;
  }

  buildInfoPayload(reservation: any) {
    const infoPayload = {
      exclude_from_invoice: 1,
      id: reservation.reservation_id.toString(),
      verification_paid: reservation.verification_paid
    }
    return infoPayload;
  }

  onReservationUpdatePolicyEvent(row: any) {
    this.reservationStatusUpdate = false;
    let reservationsToUpdate = row.selected;
    this.getSelectedReservationDetails(reservationsToUpdate);
  }

  openAssignReservationPolicyDialog() {
    const assignDialogRef = this.dialogRef.open(UpdatePolicyToolDialogComponent, {
      width: '90%',
      maxHeight: '750px',
      disableClose: true,
      data: { 
        policies: this.organizationPolicies,
        component: 'reservations'
      }
    });
    assignDialogRef.afterClosed().subscribe(selectedPolicyId => {
      if (selectedPolicyId > 0) {
        let newPolicyIdArr: any[] = [];
        newPolicyIdArr.push(selectedPolicyId);

        try {
          let ctr = 0;
          this.reservationsToUpdate.forEach(reservation => {
            ctr++;
            reservation.selected_policies = newPolicyIdArr;
            const reservationUpdatePayload = this.buildUpdatePolicyPayload(reservation);

            this.reservationService.updateReservation(reservation.reservation_id, reservationUpdatePayload).then(_ => {
              this.reservationService.getReservationById(reservation.reservation_id);

              if (ctr === this.reservationsToUpdate.length) {
                this.notificationService.showNotification({ message: `Reservation policies updated successfully!` })
              }
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while assigning Reservation policy', 'Error while assigning Reservation policy');
        }
      }
    });
  }

  buildUpdatePolicyPayload(reservation: Reservation) {
    let updatePolicyPayload = {
      reservation_id: reservation.reservation_id,
      reservation_reference_id: reservation.reservation_reference_id,
      arrival_date: reservation.arrival_date,
      departure_date: reservation.departure_date,
      booking_date: reservation.booking_date,
      number_of_guests: reservation.number_of_guests,
      reservation_cost: reservation.reservation_cost,
      reservation_currency_id: reservation.reservation_currency_id,
      property_id: reservation.property.property_id,
      guests: reservation.guests,
      organization_id: reservation.organization_id,
      selected_policies: reservation.selected_policies
    }
    return updatePolicyPayload;
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/reservations/detail/${row['id']}`]);
  }
}